import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ComponentsLib } from '@payface-frontends-libs/components';
import { Box, Card, Skeleton } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { merge } from '../../utils/utils';

const { BaseOptionChart } = ComponentsLib.Charts;

const LAST_DEVICES_CHECK_KEY = 'lastDevicesCheck';
const LAST_DEVICES_ACTUAL_KEY = 'lastDevicesActual';

const isSameDay = (date1: string, date2: string): boolean => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};

export function DevicesChartCard() {
  const { online, total, isLoading } = useSelector((state: RootState) => state.devices);
  const [data, setData] = useState<number[]>([]);
  const [lastCheck, setLastCheck] = useState<string>('');
  const [lastActual, setLastActual] = useState<string>('');

  const handleOptions = merge(BaseOptionChart(), {
    labels: ['Online', 'Offline'],
    legend: {
      show: false,
    },
    chart: {
      type: 'donut',
    },
    plotOptions: {
      pie: {
        donut: {
          size: '55%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '12px',
              color: '#637381',
              offsetY: 1,
            },
            value: {
              show: true,
              fontSize: '14px',
              formatter: (val: number) => `${val.toFixed(2)}%`,
            },
            total: {
              show: true,
              label: total?.toLocaleString() || '0',
              color: '#000000',
              fontSize: '24px',
              fontWeight: 600,
              offsetY: -1,
              formatter() {
                return 'Total de dispositivos';
              },
            },
          },
        },
      },
    },
    colors: ['#00AB55', '#FF4842'],
    tooltip: {
      style: {
        fontSize: '15px',
        backgroundColor: '#000000',
        color: '#FFFFFF',
        borderRadius: '4px',
        padding: '8px',
        fontWeight: 600,
        boxShadow: 'none',
        border: '0 !important',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(2)}%`,
      },
    },
  });

  useEffect(() => {
    if (online !== undefined) {
      const offline = 100 - online;
      setData([online, offline]);

      const now = new Date().toISOString();
      localStorage.setItem(LAST_DEVICES_ACTUAL_KEY, now);
      setLastActual(now);

      const savedCheck = localStorage.getItem(LAST_DEVICES_CHECK_KEY);
      if (!savedCheck || !isSameDay(savedCheck, now)) {
        localStorage.setItem(LAST_DEVICES_CHECK_KEY, now);
        setLastCheck(now);
      }
    }
  }, [online]);

  useEffect(() => {
    const savedLastCheck = localStorage.getItem(LAST_DEVICES_CHECK_KEY);
    const savedLastActual = localStorage.getItem(LAST_DEVICES_ACTUAL_KEY);

    if (savedLastCheck) {
      setLastCheck(savedLastCheck);
    }

    if (savedLastActual) {
      setLastActual(savedLastActual);
    }
  }, []);

  return (
    <Card sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 3 }}>
      {isLoading ? (
        <Skeleton
          sx={{ display: 'flex', alignSelf: 'center' }}
          animation="wave"
          variant="text"
          width={250}
          height={360}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <ReactApexChart type="donut" series={data} options={handleOptions} height={340} />
          <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: -3, alignItems: 'center' }}>
            <Box sx={{ color: '#FF6961', fontSize: '0.875rem' }}>
              Última consulta realizada: {lastCheck ? new Date(lastCheck).toLocaleString('pt-BR') : 'Não disponível'}
            </Box>
            <Box sx={{ color: '#FF6961', fontSize: '0.875rem' }}>
              Consulta mais recente: {lastActual ? new Date(lastActual).toLocaleString('pt-BR') : 'Não disponível'}
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
}
